import React, { useEffect, useRef, useState } from "react";
import { Link, NavLink, useLocation, useNavigate } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { useNotifications } from "@/context/NotificationsContext";
import { usePortal } from "./PortalContext";
import { Home, FolderOpen, CheckCircle2, FileText, Bell, CircleHelp, LogOut, Menu, X, ChevronDown, ChevronRight, CalendarDays, UserRound } from "lucide-react";
import { Avatar } from "./ui/Layout";
import PmContactModal from "./PmContactModal";
import { fmtDateLong } from "./format";
import "./theme.css";

const PRIMARY = [
  { to: "/portal", label: "Acasă", icon: Home, end: true },
  { to: "/portal/proiecte", label: "Proiecte", icon: FolderOpen },
  { to: "/portal/documente", label: "Documente", icon: FileText },
  { to: "/portal/decizii", label: "Decizii", icon: CheckCircle2, dot: "decisions" },
  { to: "/portal/notificari", label: "Notificări", icon: Bell, badge: "unread", mobileOnly: true },
];

function Wordmark({ compact = false }) {
  return (
    <Link to="/portal" aria-label="Portal Brisk — Acasă" className="flex items-center gap-3 group" data-testid="portal-wordmark">
      <img src="/brand/logo.png" alt="BRISK — One Step Ahead" className={compact ? "h-7 w-auto" : "h-9 w-auto"} />
      {!compact && (
        <span className="hidden xl:flex items-center gap-3">
          <span className="h-6 w-px bg-cp-border" aria-hidden="true" />
          <span className="text-[10px] font-semibold tracking-[0.22em] uppercase text-cp-text-3">Client Portal</span>
        </span>
      )}
    </Link>
  );
}

export default function PortalShell({ children, title, crumbs, wide = true }) {
  const { user, logout } = useAuth();
  const { unread } = useNotifications();
  const { pendingDecisions, overview } = usePortal();
  const navigate = useNavigate();
  const location = useLocation();
  const [drawer, setDrawer] = useState(false);
  const [pmOpen, setPmOpen] = useState(false);
  const [menu, setMenu] = useState(false);
  const menuRef = useRef(null);

  useEffect(() => { setDrawer(false); setMenu(false); }, [location.pathname]);
  useEffect(() => {
    document.body.classList.add("cp-theme");
    document.body.style.setProperty("--cp-bg-image", `url(${process.env.PUBLIC_URL || ""}/brand/fundal.png)`);
    return () => {
      document.body.classList.remove("cp-theme");
      document.body.style.removeProperty("--cp-bg-image");
    };
  }, []);
  useEffect(() => { if (title) document.title = `${title} · Portal Brisk`; }, [title]);
  useEffect(() => {
    if (!menu) return;
    const onDoc = (e) => { if (menuRef.current && !menuRef.current.contains(e.target)) setMenu(false); };
    document.addEventListener("mousedown", onDoc); return () => document.removeEventListener("mousedown", onDoc);
  }, [menu]);

  const counts = { decisions: pendingDecisions, unread };
  const doLogout = async () => { await logout(); navigate("/login"); };
  const pm = (overview?.projects || []).map((p) => p.pm).find((x) => x?.name);
  const trail = crumbs && crumbs.length ? crumbs : (title ? [{ label: title }] : null);

  const topNavLink = (item) => (
    <NavLink key={item.to} to={item.to} end={item.end} data-testid={`nav-${item.label.toLowerCase()}`}
      className={({ isActive }) => `cp-topnav-link ${isActive ? "is-active" : ""}`}>
      <span>{item.label}</span>
      {item.dot && counts[item.dot] > 0 && <span className="cp-topnav-dot" title={`${counts[item.dot]} în așteptare`} />}
    </NavLink>
  );

  const drawerLink = (item, onClick) => {
    const Icon = item.icon;
    return (
      <NavLink key={item.to} to={item.to} end={item.end} onClick={onClick} data-testid={`drawer-${item.label.toLowerCase()}`}
        className={({ isActive }) => `flex items-center gap-3 h-11 px-3 rounded-[var(--cp-radius)] text-[14px] font-medium transition-colors ${isActive ? "bg-cp-accent-soft text-cp-accent" : "text-cp-text-2 hover:bg-cp-surface-active"}`}>
        <Icon className="h-[18px] w-[18px]" aria-hidden="true" />
        <span className="flex-1">{item.label}</span>
        {item.dot && counts[item.dot] > 0 && <span className="cp-topnav-dot" />}
        {item.badge && counts[item.badge] > 0 && <span className="min-w-[20px] h-5 px-1.5 rounded-full text-[11px] font-bold grid place-items-center bg-cp-amber text-white">{counts[item.badge] > 99 ? "99+" : counts[item.badge]}</span>}
      </NavLink>
    );
  };

  return (
    <div className="cp min-h-screen flex flex-col">
      {/* Top navbar */}
      <header className="cp-topnav sticky top-0 z-[30] h-[var(--cp-topbar-h)]" data-testid="portal-topnav">
        <div className="mx-auto max-w-[var(--cp-content-max)] h-full px-4 sm:px-6 lg:px-8 flex items-center gap-4">
          <div className="lg:hidden"><Wordmark compact /></div>
          <div className="hidden lg:block"><Wordmark /></div>

          <nav aria-label="Navigație principală" className="hidden lg:flex items-center gap-7 ml-8">
            {PRIMARY.filter((i) => !i.mobileOnly).map(topNavLink)}
          </nav>

          <div className="ml-auto flex items-center gap-1.5 sm:gap-2.5">
            <span className="hidden md:inline-flex cp-datepill" data-testid="topbar-date">
              <CalendarDays className="h-4 w-4 text-cp-text-3" aria-hidden="true" />{fmtDateLong(new Date().toISOString())}
            </span>
            <button type="button" onClick={() => navigate("/portal/notificari")} data-testid="notif-bell" aria-label={`Notificări${unread ? `, ${unread} necitite` : ""}`} className="cp-iconbtn">
              <Bell className="h-[18px] w-[18px]" />
              {unread > 0 && <span data-testid="notif-badge" className="absolute top-1 right-1 min-w-[16px] h-4 px-1 rounded-full bg-cp-amber text-white text-[10px] font-bold grid place-items-center ring-2 ring-white">{unread > 99 ? "99+" : unread}</span>}
            </button>
            <div className="relative hidden lg:block" ref={menuRef}>
              <button type="button" onClick={() => setMenu((v) => !v)} data-testid="user-menu" aria-haspopup="menu" aria-expanded={menu}
                className="flex items-center gap-2.5 pl-1 pr-2 h-10 rounded-[var(--cp-radius)] hover:bg-cp-surface-active transition-colors">
                <Avatar name={user?.name} size="sm" />
                <span className="text-[13.5px] font-medium text-cp-text max-w-[160px] truncate">{user?.name}</span>
                <ChevronDown className={`h-4 w-4 text-cp-text-3 transition-transform ${menu ? "rotate-180" : ""}`} aria-hidden="true" />
              </button>
              {menu && (
                <div role="menu" className="absolute right-0 mt-1.5 w-60 cp-panel shadow-[var(--cp-shadow-lg)] p-1.5 z-[40] cp-rise" data-testid="user-menu-dropdown">
                  <div className="px-3 py-2 border-b border-cp-border mb-1"><div className="text-[13px] font-semibold text-cp-text truncate">{user?.name}</div><div className="cp-meta truncate">{user?.email}</div></div>
                  <Link to="/portal/cont" role="menuitem" className="flex items-center gap-2.5 px-3 h-9 rounded-[var(--cp-radius)] text-[13.5px] text-cp-text hover:bg-cp-surface-active" data-testid="menu-account"><UserRound className="h-4 w-4 text-cp-text-3" /> Contul meu</Link>
                  <button type="button" role="menuitem" onClick={() => { setMenu(false); setPmOpen(true); }} className="w-full flex items-center gap-2.5 px-3 h-9 rounded-[var(--cp-radius)] text-[13.5px] text-cp-text hover:bg-cp-surface-active text-left" data-testid="menu-help"><CircleHelp className="h-4 w-4 text-cp-text-3" /> Ajutor & contact</button>
                  <button type="button" role="menuitem" onClick={doLogout} className="w-full flex items-center gap-2.5 px-3 h-9 rounded-[var(--cp-radius)] text-[13.5px] text-cp-text hover:bg-cp-surface-active text-left" data-testid="menu-logout"><LogOut className="h-4 w-4 text-cp-text-3" /> Deconectare</button>
                </div>
              )}
            </div>
            <button type="button" onClick={() => setDrawer(true)} data-testid="mobile-menu-btn" aria-label="Meniu" aria-expanded={drawer} className="lg:hidden cp-iconbtn">
              <Menu className="h-5 w-5" />
            </button>
          </div>
        </div>
      </header>

      <main className="flex-1 px-4 sm:px-6 lg:px-8 py-6 lg:py-8 pb-[calc(var(--cp-bottomnav-h)+24px+env(safe-area-inset-bottom,0px))] lg:pb-10">
        <div className={`mx-auto w-full ${wide ? "max-w-[var(--cp-content-max)]" : "max-w-[1100px]"}`}>
          {trail && (
            <nav aria-label="Breadcrumb" className="mb-4">
              <ol className="flex items-center gap-1.5 text-[12.5px] text-cp-text-2" data-testid="breadcrumb">
                {trail.map((c, i) => (
                  <li key={i} className="flex items-center gap-1.5 min-w-0">
                    {i > 0 && <ChevronRight className="h-3.5 w-3.5 text-cp-text-3 shrink-0" aria-hidden="true" />}
                    {c.to ? <Link to={c.to} className="hover:text-cp-accent truncate transition-colors">{c.label}</Link>
                      : <span className={`truncate ${i === trail.length - 1 ? "text-cp-text font-medium" : ""}`} aria-current={i === trail.length - 1 ? "page" : undefined}>{c.label}</span>}
                  </li>
                ))}
              </ol>
            </nav>
          )}
          {children}
        </div>
      </main>

      <footer className="hidden lg:block border-t border-cp-border">
        <div className="mx-auto max-w-[var(--cp-content-max)] px-8 py-5 flex items-center justify-between text-[12px] text-cp-text-3">
          <span>© {new Date().getFullYear()} Brisk Group · Consulting &amp; Development</span>
          <span>Portal dedicat proiectelor. Construit pentru colaborare.</span>
        </div>
      </footer>

      {/* Bottom nav (mobil/tabletă) */}
      <nav aria-label="Navigație" data-testid="bottom-nav"
        className="lg:hidden fixed bottom-0 inset-x-0 z-[30] bg-white border-t border-cp-border pb-[max(0px,calc(env(safe-area-inset-bottom,0px)-12px))]">
        <div className="h-[var(--cp-bottomnav-h)] flex">
          {PRIMARY.map((item) => {
            const Icon = item.icon; const n = counts[item.badge] ?? counts[item.dot];
            return (
              <NavLink key={item.to} to={item.to} end={item.end} data-testid={`bottom-${item.label.toLowerCase()}`}
                className={({ isActive }) => `flex flex-col items-center justify-center gap-1 flex-1 text-[11px] font-medium relative transition-colors ${isActive ? "text-cp-accent" : "text-cp-text-3"}`}>
                <span className="relative">
                  <Icon className="h-5 w-5" aria-hidden="true" />
                  {n > 0 && <span className={`absolute -top-1.5 -right-2 min-w-[14px] h-3.5 px-1 rounded-full text-white text-[9px] font-bold grid place-items-center ${item.badge ? "bg-cp-amber" : "bg-cp-amber"}`}>{n > 9 ? "9+" : n}</span>}
                </span>
                {item.label}
              </NavLink>
            );
          })}
        </div>
      </nav>

      {/* Drawer (mobil) */}
      {drawer && (
        <div className="lg:hidden fixed inset-0 z-[60]" role="dialog" aria-modal="true" aria-label="Meniu" data-testid="mobile-drawer">
          <div className="absolute inset-0 bg-[#0F1C2E]/40" onClick={() => setDrawer(false)} />
          <aside className="cp absolute right-0 top-0 h-full w-[300px] max-w-[85vw] bg-white shadow-[var(--cp-shadow-lg)] flex flex-col animate-in slide-in-from-right-4 duration-200">
            <div className="h-[var(--cp-topbar-h)] flex items-center justify-between px-5 border-b border-cp-border">
              <Wordmark />
              <button type="button" onClick={() => setDrawer(false)} aria-label="Închide meniul" data-testid="mobile-close" className="cp-iconbtn"><X className="h-5 w-5" /></button>
            </div>
            <nav className="px-3 pt-4 space-y-1" aria-label="Navigație">{PRIMARY.map((i) => drawerLink(i, () => setDrawer(false)))}</nav>
            <div className="mt-auto px-3 pb-5 space-y-1 border-t border-cp-border pt-4">
              {pm && (
                <button type="button" onClick={() => { setDrawer(false); setPmOpen(true); }} data-testid="drawer-pm" className="w-full flex items-center gap-3 px-3 py-2 rounded-[var(--cp-radius)] hover:bg-cp-surface-active text-left">
                  <Avatar name={pm.name} src={pm.avatar_url ? `${process.env.REACT_APP_BACKEND_URL}${pm.avatar_url}` : undefined} size="md" />
                  <span className="min-w-0"><span className="block text-[13.5px] font-semibold text-cp-text truncate">{pm.name}</span><span className="block text-[12px] text-cp-text-3 truncate">{pm.job_title || "Project Manager"}</span></span>
                </button>
              )}
              <Link to="/portal/cont" onClick={() => setDrawer(false)} data-testid="drawer-cont" className="flex items-center gap-3 h-11 px-3 rounded-[var(--cp-radius)] text-[14px] font-medium text-cp-text-2 hover:bg-cp-surface-active"><UserRound className="h-[18px] w-[18px]" /> Contul meu</Link>
              <button type="button" onClick={doLogout} data-testid="logout-btn" className="w-full flex items-center gap-3 h-11 px-3 rounded-[var(--cp-radius)] text-[14px] font-medium text-cp-text-2 hover:bg-cp-surface-active text-left"><LogOut className="h-[18px] w-[18px]" /> Deconectare</button>
            </div>
          </aside>
        </div>
      )}

      <PmContactModal open={pmOpen} onOpenChange={setPmOpen} />
    </div>
  );
}
